import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';

import {
  DeleteGachaTemplateMutation,
  DeleteGachaTemplateMutationVariables,
  GachaTemplateDataFragment,
  GachaTemplateKind,
  GetGachaTemplatesQuery,
  GetGachaTemplatesQueryVariables,
  SaveGachaTemplateMutation,
  SaveGachaTemplateMutationVariables,
} from '../../../../../../__gqltypes__';
import { DELETE_GACHA_TEMPLATE, GET_GACHA_TEMPLATES, SAVE_GACHA_TEMPLATE } from '../graphql';
import {
  formatUtc,
  GachaCountRowDraft,
  GachaPoolRowDraft,
  toGachaCountRowDrafts,
  toGachaCountRowInputs,
  toGachaPoolRowDrafts,
  toGachaPoolRowInputs,
} from '../utils';
import {
  GACHA_MAX_NAME_LENGTH,
  validateGachaCountRows,
  validateGachaPoolRows,
  validateGachaTemplateName,
} from '../validation';

type CountTemplateProps = {
  kind: GachaTemplateKind.COUNT;
  rows: GachaCountRowDraft[];
  onLoad: (rows: GachaCountRowDraft[]) => void;
};
type PoolTemplateProps = {
  kind: GachaTemplateKind.REWARDS | GachaTemplateKind.MYSTERY;
  rows: GachaPoolRowDraft[];
  onLoad: (rows: GachaPoolRowDraft[]) => void;
};
type Props = CountTemplateProps | PoolTemplateProps;

// Narrows the props union by its kind (TypeScript 4.5 does not narrow a destructured union).
const isCountTemplate = (templateProps: Props): templateProps is CountTemplateProps =>
  templateProps.kind === GachaTemplateKind.COUNT;

const KIND_LABELS: Record<GachaTemplateKind, string> = {
  [GachaTemplateKind.COUNT]: 'reward-count list',
  [GachaTemplateKind.REWARDS]: 'box reward table',
  [GachaTemplateKind.MYSTERY]: 'mystery reward table',
};

function templateSummary(template: GachaTemplateDataFragment): string {
  if (template.kind === GachaTemplateKind.COUNT) {
    return (template.countRows ?? []).map((row) => `${row.count} → weight ${row.weight}`).join(', ');
  }
  const rows = template.poolRows ?? [];
  return `${rows.length} reward${rows.length === 1 ? '' : 's'}: ${rows.map((row) => row.reward.type).join(', ')}`;
}

/**
 * Save as template / Load template for one of the three tables. Templates are stored per kind (COUNT, REWARDS,
 * MYSTERY); loading one replaces the table's rows in the editor (saved with the campaign).
 */
export function GachaTemplateControls(props: Props): JSX.Element {
  const { kind } = props;
  // Runs the branch of the props variant, with its rows/onLoad typed for that variant.
  const byKind = <T,>(onCount: (count: CountTemplateProps) => T, onPool: (pool: PoolTemplateProps) => T): T =>
    isCountTemplate(props) ? onCount(props) : onPool(props);
  const label = KIND_LABELS[kind];
  const [mode, setMode] = useState<'load' | 'save' | null>(null);
  const [name, setName] = useState('');
  const [message, setMessage] = useState<{ variant: string; text: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<GachaTemplateDataFragment | null>(null);

  const { data, loading, refetch } = useQuery<GetGachaTemplatesQuery, GetGachaTemplatesQueryVariables>(
    GET_GACHA_TEMPLATES,
    { variables: { kind }, skip: mode === null, fetchPolicy: 'no-cache' }
  );
  const [saveTemplate, { loading: saving }] = useMutation<
    SaveGachaTemplateMutation,
    SaveGachaTemplateMutationVariables
  >(SAVE_GACHA_TEMPLATE);
  const [deleteTemplate, { loading: deleting }] = useMutation<
    DeleteGachaTemplateMutation,
    DeleteGachaTemplateMutationVariables
  >(DELETE_GACHA_TEMPLATE);

  const templates = data?.liveOps.gachaTemplates ?? [];
  const sameName = templates.find((template) => template.name.trim().toLowerCase() === name.trim().toLowerCase());
  const rowErrors = byKind(
    ({ rows }) => validateGachaCountRows(rows, 'Template'),
    ({ rows }) => validateGachaPoolRows(rows, 'Template')
  );
  const saveErrors = [...validateGachaTemplateName(name), ...rowErrors];

  const close = () => {
    setMode(null);
    setMessage(null);
    setConfirmDelete(null);
  };

  const load = (template: GachaTemplateDataFragment) => {
    byKind(
      ({ onLoad }) => onLoad(toGachaCountRowDrafts(template.countRows ?? [])),
      ({ onLoad }) => onLoad(toGachaPoolRowDrafts(template.poolRows ?? []))
    );
    close();
  };

  const save = () => {
    setMessage(null);
    saveTemplate({
      variables: {
        input: {
          // Same name = overwrite that template.
          id: sameName?.id ?? null,
          kind,
          name: name.trim(),
          countRows: byKind(
            ({ rows }) => toGachaCountRowInputs(rows),
            () => null
          ),
          poolRows: byKind(
            () => null,
            ({ rows }) => toGachaPoolRowInputs(rows)
          ),
        },
      },
    })
      .then(() => {
        setMessage({ variant: 'success', text: `Template "${name.trim()}" saved.` });
        setName('');
        refetch();
      })
      .catch((error) => setMessage({ variant: 'danger', text: error.message }));
  };

  const remove = (template: GachaTemplateDataFragment) => {
    setMessage(null);
    deleteTemplate({ variables: { id: template.id } })
      .then(() => refetch())
      .catch((error) => setMessage({ variant: 'danger', text: error.message }))
      .finally(() => setConfirmDelete(null));
  };

  return (
    <div className="d-flex">
      <Button size="sm" variant="outline-secondary" className="mr-1" onClick={() => setMode('load')}>
        <i className="fas fa-folder-open mr-1" />
        Load template
      </Button>
      <Button size="sm" variant="outline-secondary" onClick={() => setMode('save')}>
        <i className="fas fa-save mr-1" />
        Save as template
      </Button>

      <Modal show={mode !== null} onHide={close} size="lg">
        <Modal.Header closeButton>
          <Modal.Title as="h5">
            {mode === 'save' ? `Save this ${label} as a template` : `Load a ${label} template`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {message && <Alert variant={message.variant}>{message.text}</Alert>}
          {mode === 'save' && (
            <div className="mb-3">
              <Form.Label htmlFor={`gacha-template-name-${kind}`}>Template name</Form.Label>
              <Form.Control
                id={`gacha-template-name-${kind}`}
                value={name}
                maxLength={GACHA_MAX_NAME_LENGTH}
                onChange={(e) => setName(e.target.value)}
              />
              {sameName && (
                <Form.Text className="text-warning">A template with this name exists: saving overwrites it.</Form.Text>
              )}
              {name.trim() !== '' && saveErrors.length > 0 && (
                <ul className="small text-danger mt-2 mb-0">
                  {saveErrors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {loading ? (
            <Spinner animation="border" />
          ) : templates.length === 0 ? (
            <div className="text-muted">No {label} template yet.</div>
          ) : (
            <Table size="sm" bordered responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Content</th>
                  <th style={{ width: 170 }}>Updated</th>
                  <th style={{ width: 130 }} />
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.id}>
                    <td>{template.name}</td>
                    <td className="small">{templateSummary(template)}</td>
                    <td className="small">{formatUtc(template.updatedAt)}</td>
                    <td className="text-right">
                      {mode === 'load' && (
                        <Button size="sm" variant="primary" className="mr-1" onClick={() => load(template)}>
                          Load
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline-danger"
                        title="Delete the template"
                        onClick={() => setConfirmDelete(template)}
                      >
                        <i className="fas fa-trash" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {mode === 'load' && templates.length > 0 && (
            <Form.Text className="text-muted">
              Loading replaces every row of this table in the editor. Nothing is stored until you save the campaign.
            </Form.Text>
          )}
          {confirmDelete && (
            <Alert variant="danger" className="mt-2 mb-0 d-flex justify-content-between align-items-center">
              <span>
                Delete the template <strong>{confirmDelete.name}</strong>? Campaigns that loaded it keep their rows.
              </span>
              <span>
                <Button size="sm" variant="secondary" className="mr-1" onClick={() => setConfirmDelete(null)}>
                  Cancel
                </Button>
                <Button size="sm" variant="danger" disabled={deleting} onClick={() => remove(confirmDelete)}>
                  Delete
                </Button>
              </span>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close}>
            Close
          </Button>
          {mode === 'save' && (
            <Button variant="success" onClick={save} disabled={saving || saveErrors.length > 0}>
              {saving ? <Spinner animation="border" size="sm" /> : sameName ? 'Overwrite template' : 'Save template'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
